import type { Icon } from "@phosphor-icons/react";
import {
  BuildingsIcon,
  CalendarCheckIcon,
  CalendarDotsIcon,
  CertificateIcon,
  ChartBarIcon,
  ClipboardTextIcon,
  ClockIcon,
  GasPumpIcon,
  GearIcon,
  WrenchIcon,
  GiftIcon,
  HandCoinsIcon,
  HeadsetIcon,
  IdentificationCardIcon,
  ScrollIcon,
  MapPinIcon,
  MedalIcon,
  MoneyIcon,
  MotorcycleIcon,
  ProhibitIcon,
  ShieldCheckeredIcon,
  SquaresFourIcon,
  TargetIcon,
  UsersIcon,
  WalletIcon,
} from "@phosphor-icons/react";
import type { StaffRole } from "@/lib/jwt-claims";

/**
 * THE role→route matrix for the admin dashboard. One entry per route.
 *
 * This file exists because the same information used to live in three
 * hand-synced places — `<ProtectedRoute allow={...}>` in App.tsx, `NAV_ITEMS`
 * in AppSidebar.tsx, and `ROUTE_ROLES` in route-access.ts — kept in step by a
 * comment saying they "MUST mirror". They had already drifted: three routes
 * were missing from route-access.ts entirely. Admin is the only surface a hub
 * manager has (duty schedules, qualifications), so a drift here locks a real
 * person out of a real job.
 *
 * `permissions.test.ts` cross-checks this matrix against the routes actually
 * rendered in App.tsx. Without that test this is just a fourth list.
 *
 * ⚠ UI gating only — this is NOT the security boundary. RLS (driven by the
 * `role`/`hub_id` JWT claims) is, and the Edge Functions re-check the role in
 * `withAuth`. A role sneaking past this file still hits empty results or a
 * Postgres permission error.
 */

export type NavSection =
  | "Home"
  | "Operations"
  | "Team"
  | "Finance"
  | "Reports"
  | "Setup"
  | "Utility";

export const SECTION_ORDER: readonly NavSection[] = [
  "Home",
  "Operations",
  "Team",
  "Finance",
  "Reports",
  "Setup",
  "Utility",
] as const;

/**
 * Which sidebar group a route belongs to, so the top bar can name the group
 * instead of repeating the page title the page body already shows.
 *
 * A path inherits its group's section by prefix — `/hubs/<uuid>` lands in Setup
 * via `/hubs` — because a detail page still belongs to the group its list is
 * in. A query string is ignored, and an unmatched path returns null so the bar
 * shows nothing rather than guessing.
 */
export function navSectionFor(pathname: string): NavSection | null {
  const path = pathname.split("?")[0] ?? pathname;
  const best = ROUTE_PERMISSIONS.filter(
    (entry) =>
      entry.nav !== undefined && (path === entry.path || path.startsWith(`${entry.path}/`)),
  ).sort((a, b) => b.path.length - a.path.length)[0];
  return best?.nav?.section ?? null;
}

/**
 * Every staff role that can hold a dashboard session.
 *
 * `hr` and `executive` are still here because they still exist in the database
 * `user_role` enum. `hr` no longer routes anywhere (Leon, 2026-09-17: the three
 * dashboard roles are Hub Coordinator, Admin Manager and Super Admin);
 * `executive` still approves mileage rate requests, and whether it is retired
 * is DASH-5, open for Luigi.
 */
export const ALL_STAFF_ROLES: readonly StaffRole[] = [
  "hub_manager",
  "hr",
  "admin",
  "executive",
  "superadmin",
] as const;

/** Luigi's names for the three dashboard roles (Dashboard Access Standard, 2026-09-21). */
export const ROLE_LABELS: Record<StaffRole, string> = {
  hub_manager: "Hub Coordinator",
  admin: "Admin Manager",
  superadmin: "Super Admin",
  // Not named by the standard; labelled from the enum until DASH-5 resolves.
  hr: "HR",
  executive: "Executive",
};

export interface NavMeta {
  label: string;
  section: NavSection;
  icon: Icon;
}

export interface RoutePermission {
  /** Path as `canAccess` callers spell it — leading slash, no trailing slash. */
  path: string;
  /** Who may reach it. Omit a role here and it is unreachable AND hidden. */
  roles: readonly StaffRole[];
  /** Omitted = reachable but never listed in the sidebar. */
  nav?: NavMeta;
}

export const ROUTE_PERMISSIONS: readonly RoutePermission[] = [
  {
    // ⚠ Never give this route a restrictive role list. ProtectedRoute sends a
    // denied user to "/", so a role excluded from "/" redirects to itself
    // forever. It is the fallback destination and must stay reachable by every
    // staff role that can hold a session.
    path: "/",
    roles: ALL_STAFF_ROLES,
    nav: { label: "Ops Dashboard", section: "Home", icon: SquaresFourIcon },
  },
  {
    path: "/bookings",
    roles: ["hub_manager", "admin", "executive", "superadmin"],
    nav: { label: "Bookings", section: "Operations", icon: CalendarDotsIcon },
  },
  {
    path: "/attendance",
    roles: ["hub_manager", "admin", "superadmin"],
    nav: { label: "Attendance", section: "Team", icon: ClockIcon },
  },
  {
    path: "/dispatch",
    roles: ["hub_manager", "admin", "superadmin"],
    nav: { label: "Dispatch", section: "Operations", icon: MapPinIcon },
  },
  {
    path: "/calendar",
    roles: ["hub_manager", "admin", "superadmin"],
    nav: { label: "Calendar", section: "Operations", icon: CalendarCheckIcon },
  },
  {
    path: "/live-map",
    roles: ["hub_manager", "admin", "superadmin"],
    nav: { label: "Live Map", section: "Operations", icon: TargetIcon },
  },
  {
    // BL-16 / 0315 — SOS alerts and support tickets. The scope is the view's:
    // a hub coordinator sees their hub, HQ sees all. Urgency changes are
    // admin/superadmin only, in SQL.
    path: "/support",
    roles: ["hub_manager", "admin", "executive", "superadmin"],
    nav: { label: "Support", section: "Operations", icon: HeadsetIcon },
  },
  {
    path: "/rider-sessions",
    roles: ["hub_manager", "admin", "executive", "superadmin"],
    nav: { label: "Rider Sessions", section: "Team", icon: MotorcycleIcon },
  },
  {
    // ⚠ admin + superadmin ONLY. CORE-05A §7.4 gives Hub Manager VISIBILITY of
    // the approval state, and 0075 RLS grants exactly that — but the page only
    // exists to DECIDE, and TSESSION-022 names Admin. Listing a destination
    // that bounces the person who clicks it is worse than not listing it: it
    // advertises authority the Edge route refuses. A read-only hub view is a
    // different screen, not a wider role list on this one.
    path: "/therapist-sessions",
    roles: ["admin", "superadmin"],
    nav: { label: "Therapist Sessions", section: "Team", icon: CalendarCheckIcon },
  },
  {
    path: "/roster",
    roles: ["hub_manager", "admin", "superadmin"],
    nav: { label: "Roster", section: "Team", icon: UsersIcon },
  },
  {
    path: "/schedules",
    roles: ["hub_manager"],
    nav: { label: "Schedules", section: "Team", icon: CalendarDotsIcon },
  },
  {
    // Reachable but deliberately not in the sidebar — it has never been listed.
    path: "/documents",
    roles: ["admin", "superadmin"],
  },
  {
    path: "/cash",
    roles: ["hub_manager", "admin", "superadmin"],
    nav: { label: "Cash Remittance", section: "Finance", icon: MoneyIcon },
  },
  {
    path: "/mileage",
    roles: ["admin", "executive", "superadmin"],
    nav: { label: "Mileage Rate", section: "Finance", icon: GasPumpIcon },
  },
  {
    path: "/payroll",
    roles: ["admin", "executive", "superadmin"],
    nav: { label: "Payroll", section: "Finance", icon: WalletIcon },
  },
  {
    // One page for the daily GiftPass work (Leon, 2026-10-30): orders, print
    // queue, cards and packages as tabs. Admin and superadmin read and run the
    // print queue; manual orders, mark as paid and package edits are
    // Superadmin (server). The records live in Executive logs.
    path: "/giftpass",
    roles: ["admin", "superadmin"],
    nav: { label: "GiftPass", section: "Finance", icon: GiftIcon },
  },
  {
    // Old address, kept so a saved link still works: it redirects to /giftpass.
    path: "/giftpass-orders",
    roles: ["admin", "superadmin"],
  },
  {
    // Leon 2026-10-16: executive logs (Luigi's workbook) — Superadmin only, client ID only.
    path: "/exec-logs",
    roles: ["superadmin"],
    nav: { label: "Executive logs", section: "Finance", icon: ScrollIcon },
  },
  {
    // Leon 2026-10-09: only a Superadmin sends money out of the PayMongo wallet.
    path: "/payouts",
    roles: ["superadmin"],
    nav: { label: "Payouts", section: "Finance", icon: HandCoinsIcon },
  },
  {
    path: "/analytics",
    roles: ["admin", "executive", "superadmin"],
    nav: { label: "Analytics", section: "Reports", icon: ChartBarIcon },
  },
  {
    path: "/catalog",
    roles: ["admin", "superadmin"],
    nav: { label: "Service Catalog", section: "Setup", icon: ClipboardTextIcon },
  },
  {
    path: "/hubs",
    roles: ["superadmin"],
    nav: { label: "Hubs", section: "Setup", icon: BuildingsIcon },
  },
  {
    // Detail route behind /hubs. Listed so the App.tsx cross-check sees it;
    // `canAccess` is exact-match, so a concrete "/hubs/<uuid>" never resolves
    // here — no caller asks it to.
    path: "/hubs/:hubId",
    roles: ["superadmin"],
  },
  {
    // Oct 11 meeting. Superadmin only: the rank rules (0244/0333) and the perks
    // write to superadmin-gated tables, and rank-setting authority is withheld
    // from everyone else (OQ-80, never-invent-policy.md).
    path: "/rankings",
    roles: ["superadmin"],
    nav: { label: "Rankings", section: "Team", icon: MedalIcon },
  },
  {
    // 0309 (Luigi BL-03): a hub coordinator requests, an HQ admin decides.
    path: "/restrictions",
    roles: ["hub_manager", "admin", "superadmin"],
    nav: { label: "Restrictions", section: "Team", icon: ProhibitIcon },
  },
  {
    // Superadmin only, mirroring grant_service_qualification's own grant
    // (0037). Who may certify a therapist is undefined in the vault
    // (SECURITY §9a) — the capability is built and the authority withheld
    // until Luigi assigns it.
    path: "/qualifications",
    roles: ["superadmin"],
    nav: { label: "Qualifications", section: "Team", icon: CertificateIcon },
  },
  {
    // Superadmin only, mirroring the two admin KYC Edge routes' own gate. Who
    // may approve a client's identity is an Open Item (OQ-81) — capability
    // built, authority withheld (never-invent-policy.md).
    //
    // ⚠ Luigi's Dashboard Access Standard (2026-09-21) assigns client KYC to
    // the Admin Manager. That is DASH-4 and it is a deliberate widening of a
    // server-side control, so it lands with the Edge gate and its pgTAP — not
    // by editing this line alone.
    path: "/kyc",
    roles: ["superadmin"],
    nav: { label: "ID Verification", section: "Setup", icon: IdentificationCardIcon },
  },
  {
    // BL-25 — 0288 grants legal writes to superadmin alone.
    path: "/legal",
    roles: ["superadmin"],
    nav: { label: "Terms & Privacy", section: "Setup", icon: ScrollIcon },
  },
  {
    path: "/audit",
    roles: ["superadmin"],
    nav: { label: "Audit Log", section: "Utility", icon: ShieldCheckeredIcon },
  },
  {
    // App-wide operating numbers — Luigi, 2026-09-11: "yan lang siguro need mga
    // app wide na settings sa super admin". The write policies on both tables
    // say the same thing, so the nav and the database agree.
    path: "/settings",
    roles: ["superadmin"],
    nav: { label: "Settings", section: "Utility", icon: GearIcon },
  },
  {
    // 0383 — maintenance and the minimum app version. Super Admin only (Leon,
    // 2026-10-30); the table's update policy says the same.
    path: "/app-status",
    roles: ["superadmin"],
    nav: { label: "App status", section: "Utility", icon: WrenchIcon },
  },
] as const;

const BY_PATH = new Map(ROUTE_PERMISSIONS.map((entry) => [entry.path, entry]));

/** Roles allowed on a route, for `<ProtectedRoute allow={...}>`. */
export function rolesFor(path: string): StaffRole[] {
  const entry = BY_PATH.get(path);
  if (!entry) throw new Error(`No permission entry for route "${path}"`);
  return [...entry.roles];
}

export function canAccess(role: StaffRole, path: string): boolean {
  return BY_PATH.get(path)?.roles.includes(role) ?? false;
}

/** Returns the href when the role may visit it, else undefined (hides the link). */
export function hrefFor(role: StaffRole, path: string): string | undefined {
  return canAccess(role, path) ? path : undefined;
}

export interface NavItem extends NavMeta {
  to: string;
}

/** Sidebar entries this role may see, in matrix order within each section. */
export function navItemsFor(role: StaffRole): NavItem[] {
  return ROUTE_PERMISSIONS.filter(
    (entry): entry is RoutePermission & { nav: NavMeta } =>
      entry.nav !== undefined && entry.roles.includes(role),
  ).map((entry) => ({ to: entry.path, ...entry.nav }));
}
